// =====================================================================
// CrudDialog — modal de form generico (Fase 3, S38).
// Declarativo: voce passa `fields` (array) e ele renderiza o form.
// Suporta types padrao + `custom` (render livre via callback).
//
// fields: [{
//   key,          // string (nome do campo no payload)
//   label,        // string
//   type,         // 'text'|'number'|'switch'|'select'|'textarea'|'color'|'url'|'custom'
//   required?,    // bool
//   placeholder?, helperText?,
//   options?,     // pra select: [{ value, label }]
//   rows?,        // pra textarea: numero de linhas (default 4)
//   colSpan?,     // 1..12 (Grid xs); default 12 ou 6
//   regex?,       // RegExp pra validacao client
//   regexError?,  // mensagem se regex falhar
//   render?,      // pra custom: (value, setValue, allValues, setField) => ReactNode
//                 //   setField(key, value) pode setar QUALQUER field (ex: TemplatePreview
//                 //   que controla template_text + variables ao mesmo tempo)
//   transform?    // (raw) => sanitized (ex: Number(x) pra number)
// }]
//
// Props:
//   open, onClose, onSave(payload), title, fields, initialValues, saving
// =====================================================================
import React, { useState, useEffect, useCallback, useMemo } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Button,
  CircularProgress,
  Typography,
  Box,
  Divider
} from "@material-ui/core";
import useIsMobile from "../../../utils/useIsMobile";

const useStyles = makeStyles((theme) => ({
  paper: {
    borderRadius: 12,
    [theme.breakpoints.up("sm")]: { minWidth: 640, maxWidth: 880 }
  },
  title: {
    paddingBottom: 0
  },
  content: {
    paddingTop: theme.spacing(2),
    ...theme.scrollbarStylesSoft
  },
  colorRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5)
  },
  colorPicker: {
    width: 44,
    height: 44,
    padding: 0,
    border: "none",
    background: "transparent",
    cursor: "pointer"
  },
  customWrap: {
    marginTop: theme.spacing(0.5)
  },
  customLabel: {
    fontSize: "0.75rem",
    fontWeight: 600,
    color: theme.palette.text.secondary,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    marginBottom: theme.spacing(0.5),
    display: "block"
  },
  actions: {
    padding: theme.spacing(2, 3),
    borderTop: `1px solid ${theme.palette.divider}`
  }
}));

const CrudDialog = ({
  open,
  onClose,
  onSave,
  title,
  fields,
  initialValues,
  saving
}) => {
  const classes = useStyles();
  const isMobile = useIsMobile();
  const [values, setValues] = useState({});
  const [errors, setErrors] = useState({});

  // Reset quando abre/troca initialValues
  useEffect(() => {
    if (open) {
      setValues(initialValues || {});
      setErrors({});
    }
  }, [open, initialValues]);

  const setField = useCallback((key, raw) => {
    setValues((prev) => ({ ...prev, [key]: raw }));
    setErrors((prev) => ({ ...prev, [key]: undefined }));
  }, []);

  const validate = useCallback(() => {
    const out = {};
    fields.forEach((f) => {
      const v = values[f.key];
      if (f.required) {
        if (v === undefined || v === null || v === "") {
          out[f.key] = "Campo obrigatório";
          return;
        }
      }
      if (
        f.regex &&
        v !== undefined &&
        v !== null &&
        v !== "" &&
        !f.regex.test(String(v))
      ) {
        out[f.key] = f.regexError || "Formato inválido";
      }
    });
    setErrors(out);
    return Object.keys(out).length === 0;
  }, [fields, values]);

  const handleSave = () => {
    if (!validate()) return;
    // Aplica transforms (number, etc) antes de mandar
    const payload = {};
    fields.forEach((f) => {
      const v = values[f.key];
      if (v === undefined) return;
      if (v === "" && f.type !== "color" && f.type !== "url" && f.type !== "text" && f.type !== "textarea") {
        // String vazia em campos nao-string vira null
        payload[f.key] = null;
        return;
      }
      payload[f.key] = f.transform ? f.transform(v) : v;
    });
    onSave(payload);
  };

  const renderField = (f) => {
    const v = values[f.key];
    const err = errors[f.key];

    if (f.type === "custom" && typeof f.render === "function") {
      return (
        <div className={classes.customWrap}>
          <span className={classes.customLabel}>{f.label}{f.required ? " *" : ""}</span>
          {f.render(
            v,
            (newVal) => setField(f.key, newVal),
            values,
            setField
          )}
          {(err || f.helperText) && (
            <Typography
              variant="caption"
              color={err ? "error" : "textSecondary"}
              style={{ display: "block", marginTop: 4 }}
            >
              {err || f.helperText}
            </Typography>
          )}
        </div>
      );
    }

    if (f.type === "switch") {
      return (
        <FormControlLabel
          control={
            <Switch
              checked={!!v}
              onChange={(e) => setField(f.key, e.target.checked)}
              color="primary"
            />
          }
          label={f.label}
        />
      );
    }

    if (f.type === "select") {
      return (
        <FormControl variant="outlined" size="small" fullWidth error={!!err}>
          <InputLabel>{f.label}{f.required ? " *" : ""}</InputLabel>
          <Select
            value={v ?? ""}
            onChange={(e) => setField(f.key, e.target.value)}
            label={f.label}
          >
            {!f.required && <MenuItem value=""><em>—</em></MenuItem>}
            {(f.options || []).map((o) => (
              <MenuItem key={o.value} value={o.value}>
                {o.label}
              </MenuItem>
            ))}
          </Select>
          {(err || f.helperText) && (
            <Typography
              variant="caption"
              color={err ? "error" : "textSecondary"}
              style={{ marginLeft: 14, marginTop: 4 }}
            >
              {err || f.helperText}
            </Typography>
          )}
        </FormControl>
      );
    }

    if (f.type === "color") {
      return (
        <div>
          <Typography variant="caption" color="textSecondary" style={{ display: "block", marginBottom: 4 }}>
            {f.label}{f.required ? " *" : ""}
          </Typography>
          <div className={classes.colorRow}>
            <input
              type="color"
              className={classes.colorPicker}
              value={/^#[0-9a-fA-F]{6}$/.test(v || "") ? v : "#000000"}
              onChange={(e) => setField(f.key, e.target.value)}
              aria-label={f.label}
            />
            <TextField
              variant="outlined"
              size="small"
              value={v || ""}
              onChange={(e) => setField(f.key, e.target.value)}
              placeholder="#000000"
              style={{ width: 140 }}
              error={!!err}
              helperText={err || f.helperText}
            />
          </div>
        </div>
      );
    }

    if (f.type === "textarea") {
      return (
        <TextField
          label={f.label + (f.required ? " *" : "")}
          variant="outlined"
          size="small"
          fullWidth
          multiline
          rows={f.rows || 4}
          value={v ?? ""}
          onChange={(e) => setField(f.key, e.target.value)}
          placeholder={f.placeholder}
          error={!!err}
          helperText={err || f.helperText}
        />
      );
    }

    // text/number/url default
    return (
      <TextField
        label={f.label + (f.required ? " *" : "")}
        variant="outlined"
        size="small"
        fullWidth
        type={f.type === "number" ? "number" : f.type === "url" ? "url" : "text"}
        value={v ?? ""}
        onChange={(e) => setField(f.key, e.target.value)}
        placeholder={f.placeholder}
        error={!!err}
        helperText={err || f.helperText}
        inputProps={f.type === "number" ? { min: 0, step: f.step || 1 } : undefined}
      />
    );
  };

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      classes={{ paper: classes.paper }}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
    >
      <DialogTitle className={classes.title}>{title}</DialogTitle>
      <DialogContent className={classes.content}>
        <Grid container spacing={2}>
          {fields.map((f) => (
            <Grid
              item
              xs={12}
              md={f.colSpan || (f.type === "textarea" || f.type === "custom" ? 12 : 6)}
              key={f.key}
            >
              {renderField(f)}
            </Grid>
          ))}
        </Grid>
      </DialogContent>
      <DialogActions className={classes.actions}>
        <Button onClick={onClose} disabled={saving}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSave}
          disabled={saving}
          startIcon={saving ? <CircularProgress size={18} color="inherit" /> : null}
        >
          {saving ? "Salvando…" : "Salvar"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CrudDialog;
